import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, DestroyRef, EnvironmentInjector, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { NativeMarket } from '@capacitor-community/native-market';
import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonIcon, IonLabel, IonTabBar, IonTabButton, IonTabs, ModalController, Platform } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { AppUpdateService, StorageService } from '@scandium-oy/ngx-scandium';
import { zip } from 'rxjs';
import { debounceTime, filter, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { register } from 'swiper/element/bundle';
import { AppCommonModule } from './common.module';
import { SwipeDirective } from './directives/swipe/swipe.directive';
import { NavDialogComponent } from './nav/nav.dialog';
import { AreasService } from './services/areas.service';
import { ClientsService } from './services/clients.service';
import { InitializeService } from './services/initialize.service';
import { NavigationService } from './services/navigation.service';
import { SitesService } from './services/sites.service';
import { StripeService } from './services/stripe.service';
import { UsersService } from './services/users.service';
import { VersionService } from './services/version.service';
import { LANGUAGES, storageKeys } from './utility/constants';
import { Roles } from './utility/role';
import { getDuration, setStartTime } from './utility/time';

register();

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    SwipeDirective,
    IonTabs,
    IonTabBar,
    IonTabButton,
    IonLabel,
    IonIcon,
  ],
})
export class AppComponent implements OnInit {
  private areasService = inject(AreasService);
  private documentRef = inject(DOCUMENT);
  private storageService = inject(StorageService);

  private destroyRef = inject(DestroyRef);
  private lastBack: number;

  readonly hideTabs = [Roles.partner];
  role = this.usersService.role;

  loading$ = this.router.events.pipe(
    filter(
      (e) =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError,
    ),
    map((e) => e instanceof NavigationStart),
  );

  loadingSites = computed(() => this.initializeService.loadingSites());
  isLogged = computed(() => this.usersService.currentUserS() != null);

  licenseExpired$ = this.clientsService.isLicenseExpired$.pipe(
    shareReplay(1),
  );

  residentAccess$ = this.areasService.currentUserHasAccess().pipe(
    shareReplay(1),
  );

  brand$ = this.clientsService.getCurrentClient().pipe(
    filter((client) => client?.brandColor != null),
    map((client) => this.clientsService.getBrand(client)),
    startWith(this.storageService.getItem(storageKeys.brand)),
    tap((brand) => brand?.themeColor && this.documentRef.querySelector('meta[name=\'theme-color\']').setAttribute('content', brand?.themeColor)),
    tap((brand) => brand?.themeColor && this.documentRef.body.style.setProperty('--ion-color-primary', brand.themeColor)),
    shareReplay(1),
  );

  constructor(
    private appUpdateService: AppUpdateService,
    private clientsService: ClientsService,
    private initializeService: InitializeService,
    private injectorContext: EnvironmentInjector,
    private modalCtrl: ModalController,
    private navigationService: NavigationService,
    private platform: Platform,
    private router: Router,
    private sitesService: SitesService,
    private stripeService: StripeService,
    private translate: TranslateService,
    private usersService: UsersService,
    private versionService: VersionService,
  ) {
    this.platform.backButton.subscribeWithPriority(-1, () => {
      if (Date.now() - this.lastBack < 500) {
        App.exitApp();
      } else {
        this.lastBack = Date.now();
        this.navigationService.back();
      }
    });
  }

  onSwipeBack() {
    if (this.platform.is('ios') && this.platform.is('capacitor')) {
      this.navigationService.back();
    }
  }

  ngOnInit(): void {
    this.initializeApp();
  }

  private initializeApp() {
    console.info('Initialize App');
    this.setupLang();
    this.stripeService.initialize();
    SplashScreen.hide().catch((err) => {
      console.warn(err);
    });
    StatusBar.setStyle({
      style: Style.Light,
    }).catch((err) => {
      console.warn(err);
    });
    if (Capacitor.isNativePlatform()) {
      App.getInfo().then((info) => this.checkLatestVersion(info));
    }
    // Subscribe to streams
    // App updates
    this.appUpdateService.getVersionUpdates().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    // User
    this.initializeService.loadUser().pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((loggedIn) => {
      if (!loggedIn) {
        const url = this.router.url.split('?')[0];
        if (!['/sign-up', '/privacy'].includes(url) && !url.startsWith('/instructions/public') && !url.startsWith('/worker/ticket/')) {
          this.navigationService.navigateToLogin(url);
        }
      }
    });

    setStartTime();
    zip([
      this.initializeService.loadUsers(),
      this.initializeService.loadClient(),
      this.initializeService.loadContractor(),
      this.initializeService.loadSitesData(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => console.info(getDuration(), 'Data loaded'));

    toObservable(this.loadingSites, { injector: this.injectorContext }).pipe(
      filter((loading) => !loading),
      switchMap(() => this.initializeService.loadGlobalOptionsData()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => console.info(getDuration(), 'Options loaded'));
  }

  private setupLang() {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setFallbackLang(LANGUAGES[0]);
    this.translate.addLangs(LANGUAGES);
    this.translate.use(LANGUAGES[0]);
  }

  private checkLatestVersion(info: AppInfo) {
    const build = +info.build;
    const guid = Capacitor.getPlatform();
    this.versionService.get(guid).pipe(
      take(1),
    ).subscribe((version) => {
      const latestBuild = +(version?.build ?? 0);
      if (latestBuild > build) {
        Dialog.confirm({
          title: this.translate.instant('version.title'),
          message: this.translate.instant('version.message'),
          okButtonTitle: this.translate.instant('version.ok'),
          cancelButtonTitle: this.translate.instant('version.cancel'),
        }).then((result) => {
          if (result.value) {
            // Open store
            NativeMarket.openStoreListing({
              appId: 'fi.scandium.kiira',
            });
          } else {
            App.exitApp();
          }
        });
      }
    });
  }

  onHome() {
    setTimeout(() => {
      this.navigationService.navigateToHome();
    }, 50);
  }

  onSites() {
    setTimeout(() => {
      const sites = this.sitesService.getListSync();
      if (sites.length === 1) {
        this.navigationService.navigateToSite(sites[0].guid);
      } else {
        this.navigationService.navigate(['worker', 'sites']);
      }
    }, 50);
  }

  onTickets() {
    setTimeout(() => {
      this.navigationService.navigate(['worker', 'tickets']);
    }, 50);
  }

  onMenu() {
    this.modalCtrl.create({ component: NavDialogComponent, cssClass: ['modal-fullscreen'] }).then(async (m) => m.present());
  }
}
